import React, { useEffect, useState } from 'react';
import { User, Plus, Heart, Clock, ChefHat, Search, SlidersHorizontal, Calendar, ChevronDown, ChevronUp, ArrowLeft, ShoppingCart } from 'lucide-react';
import { UserProfile } from '../App';
import { NavigationDropdown } from '../components/NavigationDropdown';
interface HomePageProps {
  userProfile: UserProfile | null;
  onEditProfile: () => void;
  onCreateRecipe: () => void;
  onOpenRecipe: (recipeId: string) => void;
  onAddToCart: (recipeId: string, recipeName: string, thumbnail: string, servings: number, ingredients: any[]) => void;
  onOpenCart: () => void;
  cartItemCount: number;
  onNavigateHome: () => void;
  onNavigateLanding?: () => void;
  onGenerateRecipe: () => void;
  onImportRecipe: () => void;
  onBrowseRecipes: () => void;
  onManageGuests: () => void;
  shouldPulseCart?: boolean;
}
type RecipeCard = {
  id: string;
  thumbnail: string;
  title: string;
  description: string;
  prepTime: string;
  cookTime: string;
  difficulty: string;
  tags: string[];
  createdBy: string;
  createdByAvatar: string;
  createdOn: string;
  isFavorite: boolean;
  ingredients: Array<{
    item: string;
    amount: number;
    unit: string;
  }>;
  defaultServings: number;
};
const MOCK_RECIPES: RecipeCard[] = [{
  id: '1',
  thumbnail: '🥗',
  title: 'Mediterranean Quinoa Bowl',
  description: 'A vibrant bowl featuring quinoa, roasted vegetables, chickpeas, and a tangy lemon-tahini dressing.',
  prepTime: '25 min',
  cookTime: '15 min',
  difficulty: 'Easy',
  tags: ['Healthy', 'Vegetarian', 'Quick', 'Mediterranean'],
  createdBy: 'You',
  createdByAvatar: '👨‍🍳',
  createdOn: '2024-01-15',
  isFavorite: true,
  ingredients: [{
    item: 'quinoa, uncooked',
    amount: 1,
    unit: 'cup'
  }, {
    item: 'cherry tomatoes, halved',
    amount: 2,
    unit: 'cups'
  }, {
    item: 'cucumber, diced',
    amount: 1,
    unit: ''
  }, {
    item: 'chickpeas, drained',
    amount: 1,
    unit: 'can'
  }, {
    item: 'feta cheese, crumbled',
    amount: 0.5,
    unit: 'cup'
  }],
  defaultServings: 4
}, {
  id: '2',
  thumbnail: '🍜',
  title: 'Spicy Thai Basil Stir-Fry',
  description: 'Quick and flavorful stir-fry with fresh vegetables and aromatic Thai basil.',
  prepTime: '20 min',
  cookTime: '10 min',
  difficulty: 'Medium',
  tags: ['Spicy', 'Asian', 'Quick', 'Stir-Fry'],
  createdBy: 'You',
  createdByAvatar: '👨‍🍳',
  createdOn: '2024-01-14',
  isFavorite: false,
  ingredients: [{
    item: 'rice noodles',
    amount: 8,
    unit: 'oz'
  }, {
    item: 'bell peppers, sliced',
    amount: 2,
    unit: ''
  }, {
    item: 'Thai basil leaves',
    amount: 1,
    unit: 'cup'
  }, {
    item: 'soy sauce',
    amount: 3,
    unit: 'tbsp'
  }, {
    item: 'garlic cloves, minced',
    amount: 4,
    unit: ''
  }],
  defaultServings: 4
}, {
  id: '3',
  thumbnail: '🍄',
  title: 'Creamy Mushroom Risotto',
  description: 'Classic Italian comfort food with arborio rice, mixed mushrooms, and parmesan.',
  prepTime: '40 min',
  cookTime: '30 min',
  difficulty: 'Medium',
  tags: ['Italian', 'Comfort-Food', 'Vegetarian'],
  createdBy: 'Alex',
  createdByAvatar: '👩‍🍳',
  createdOn: '2024-01-13',
  isFavorite: true,
  ingredients: [{
    item: 'arborio rice',
    amount: 1,
    unit: 'cup'
  }, {
    item: 'mushrooms, sliced',
    amount: 1,
    unit: 'cup'
  }, {
    item: 'parmesan cheese',
    amount: 1,
    unit: 'cup'
  }, {
    item: 'onion, diced',
    amount: 1,
    unit: 'cup'
  }, {
    item: 'garlic cloves, minced',
    amount: 2,
    unit: ''
  }],
  defaultServings: 4
}, {
  id: '4',
  thumbnail: '🐟',
  title: 'Grilled Salmon with Herb Butter',
  description: 'Perfectly grilled salmon fillet topped with compound herb butter.',
  prepTime: '30 min',
  cookTime: '15 min',
  difficulty: 'Easy',
  tags: ['Seafood', 'Healthy', 'Grilled', 'High-Protein'],
  createdBy: 'You',
  createdByAvatar: '👨‍🍳',
  createdOn: '2024-01-12',
  isFavorite: false,
  ingredients: [{
    item: 'salmon fillet',
    amount: 1,
    unit: 'lb'
  }, {
    item: 'herb butter',
    amount: 1,
    unit: 'cup'
  }, {
    item: 'lemon juice',
    amount: 1,
    unit: 'tbsp'
  }, {
    item: 'dill, chopped',
    amount: 1,
    unit: 'tbsp'
  }, {
    item: 'garlic cloves, minced',
    amount: 2,
    unit: ''
  }],
  defaultServings: 4
}, {
  id: '5',
  thumbnail: '🌮',
  title: 'Vegetarian Taco Bowl',
  description: 'A colorful bowl with seasoned black beans, corn, avocado, and fresh salsa.',
  prepTime: '25 min',
  cookTime: '20 min',
  difficulty: 'Easy',
  tags: ['Mexican', 'Vegetarian', 'Bowl', 'Family-Friendly'],
  createdBy: 'Sam',
  createdByAvatar: '🥘',
  createdOn: '2024-01-11',
  isFavorite: false,
  ingredients: [{
    item: 'black beans, drained',
    amount: 1,
    unit: 'can'
  }, {
    item: 'corn, kernels',
    amount: 1,
    unit: 'cup'
  }, {
    item: 'avocado, diced',
    amount: 1,
    unit: 'cup'
  }, {
    item: 'salsa, fresh',
    amount: 1,
    unit: 'cup'
  }, {
    item: 'lime juice',
    amount: 1,
    unit: 'tbsp'
  }],
  defaultServings: 4
}];
const ALL_TAGS = ['Healthy', 'Vegetarian', 'Quick', 'Mediterranean', 'Spicy', 'Asian', 'Italian', 'Comfort-Food', 'Seafood', 'Grilled', 'High-Protein', 'Mexican', 'Bowl', 'Family-Friendly', 'Stir-Fry'];
const DIFFICULTIES = ['Easy', 'Medium', 'Hard'];
const MOCK_USERS = [{
  name: 'Chef Alex',
  avatar: '👨‍🍳'
}, {
  name: 'Sam',
  avatar: '👩‍🍳'
}, {
  name: 'Jordan',
  avatar: '🥘'
}, {
  name: 'Taylor',
  avatar: '🥗'
}, {
  name: 'Casey',
  avatar: '🌮'
}];
export function HomePage({
  userProfile,
  onEditProfile,
  onCreateRecipe,
  onOpenRecipe,
  onAddToCart,
  onOpenCart,
  cartItemCount,
  onNavigateHome,
  onNavigateLanding,
  onGenerateRecipe,
  onImportRecipe,
  onBrowseRecipes,
  onManageGuests,
  shouldPulseCart = false
}: HomePageProps) {
  const [recipes, setRecipes] = useState(MOCK_RECIPES);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedDifficulties, setSelectedDifficulties] = useState<string[]>([]);
  const [selectedCreators, setSelectedCreators] = useState<string[]>(['You']);
  const [maxCookTime, setMaxCookTime] = useState<string>('');
  const [sortBy, setSortBy] = useState<'recent' | 'oldest' | 'name'>('recent');
  const [showFilters, setShowFilters] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  // Touch handling for mobile scroll
  const [touchStart, setTouchStart] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [touchEnd, setTouchEnd] = useState<{
    x: number;
    y: number;
  } | null>(null);
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY
    });
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY
    });
  };
  const handleTouchEnd = (recipeId: string, e: React.TouchEvent) => {
    if (!touchStart) {
      return;
    }
    // If touchEnd is null, there was no movement
    if (!touchEnd) {
      onOpenRecipe(recipeId);
      return;
    }
    const deltaX = Math.abs(touchEnd.x - touchStart.x);
    const deltaY = Math.abs(touchEnd.y - touchStart.y);
    // If movement is less than 5px in any direction, treat as tap
    // Otherwise it was a scroll - prevent the click
    if (deltaX < 5 && deltaY < 5) {
      onOpenRecipe(recipeId);
    } else {
      // Prevent the click event from firing after scroll
      e.preventDefault();
    }
    // Reset touch state
    setTouchStart(null);
    setTouchEnd(null);
  };
  const handleCardClick = (recipeId: string, e: React.MouseEvent) => {
    // Only handle click on desktop (not after touch events)
    if (touchStart === null) {
      onOpenRecipe(recipeId);
    }
  };
  const toggleFavorite = (recipeId: string) => {
    setRecipes(prev => prev.map(r => r.id === recipeId ? {
      ...r,
      isFavorite: !r.isFavorite
    } : r));
  };
  const toggleTag = (tag: string) => {
    setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
  };
  const toggleDifficulty = (difficulty: string) => {
    setSelectedDifficulties(prev => prev.includes(difficulty) ? prev.filter(d => d !== difficulty) : [...prev, difficulty]);
  };
  const toggleCreator = (creator: string) => {
    setSelectedCreators(prev => prev.includes(creator) ? prev.filter(c => c !== creator) : [...prev, creator]);
  };
  // Get unique creators from recipes
  const CREATORS = Array.from(new Set(recipes.map(r => r.createdBy)));
  // Filter users for dropdown
  const filteredUsers = MOCK_USERS.filter(u => u.name.toLowerCase().includes(userSearchTerm.toLowerCase()));
  // Filter and sort recipes
  const filteredRecipes = recipes.filter(recipe => {
    // Search filter
    if (searchTerm && !recipe.title.toLowerCase().includes(searchTerm.toLowerCase()) && !recipe.description.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    // Tag filter
    if (selectedTags.length > 0 && !selectedTags.some(tag => recipe.tags.includes(tag))) {
      return false;
    }
    // Difficulty filter
    if (selectedDifficulties.length > 0 && !selectedDifficulties.includes(recipe.difficulty)) {
      return false;
    }
    // Creator filter
    if (selectedCreators.length > 0 && !selectedCreators.includes(recipe.createdBy)) {
      return false;
    }
    // Cook time filter - parse the number from "XX min" format
    if (maxCookTime) {
      const maxMinutes = parseInt(maxCookTime);
      const recipeCookMinutes = parseInt(recipe.cookTime.replace(/\D/g, ''));
      if (recipeCookMinutes > maxMinutes) {
        return false;
      }
    }
    return true;
  }).sort((a, b) => {
    if (sortBy === 'recent') return new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime();
    if (sortBy === 'oldest') return new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime();
    return a.title.localeCompare(b.title);
  });
  return <div className="min-h-screen bg-[#f5f5f7] text-[#1f2933]" data-id="element-777">
      {/* Header */}
      <header className="bg-white border-b-2 border-gray-200 sticky top-0 z-10" data-id="element-778">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between" data-id="element-779">
          <div className="flex items-center space-x-4" data-id="element-780">
            <NavigationDropdown onNavigateHome={onNavigateHome} onNavigateLanding={onNavigateLanding} onGenerateRecipe={onGenerateRecipe} onImportRecipe={onImportRecipe} onBrowseRecipes={onBrowseRecipes} onManageGuests={onManageGuests} defaultAction="home" data-id="element-781" />
            <h1 className="text-2xl font-bold text-gray-900 hidden sm:block" data-id="element-782">
              Recipe Collection
            </h1>
          </div>

          <div className="flex items-center space-x-3" data-id="element-783">
            {/* Shopping Cart Icon */}
            <button onClick={onOpenCart} className={`relative flex items-center space-x-2 px-3 py-2 border-2 border-gray-300 hover:border-gray-500 transition-colors bg-white ${shouldPulseCart ? 'animate-pulse-yellow' : ''}`} data-id="element-784">
              <ShoppingCart className="w-5 h-5 text-gray-600" data-id="element-785" />
              {cartItemCount > 0 && <span className="absolute -top-2 -right-2 w-5 h-5 bg-gray-900 text-white text-xs font-mono flex items-center justify-center" data-id="element-786">
                  {cartItemCount}
                </span>}
            </button>

            {/* Profile Dropdown */}
            <div className="relative" data-id="element-787">
              <button onClick={() => setShowProfileDropdown(!showProfileDropdown)} className="flex items-center space-x-2 px-3 py-2 border-2 border-gray-300 hover:border-gray-500 transition-colors bg-white" data-id="element-788">
                <div className="w-6 h-6 border border-gray-300 flex items-center justify-center text-sm" data-id="element-789">
                  {userProfile?.avatar || <User className="w-3 h-3 text-gray-400" data-id="element-790" />}
                </div>
                <span className="font-mono text-xs text-gray-600 hidden sm:inline" data-id="element-791">
                  {userProfile?.name || 'User'}
                </span>
                <ChevronDown className="w-3 h-3 text-gray-400" data-id="element-792" />
              </button>

              {/* Profile Dropdown */}
              {showProfileDropdown && <div className="absolute right-0 mt-2 w-64 bg-white border-2 border-gray-300 shadow-lg z-20" data-id="element-793">
                  <div className="p-3 border-b border-gray-200" data-id="element-794">
                    <div className="relative" data-id="element-795">
                      <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" data-id="element-796" />
                      <input type="text" value={userSearchTerm} onChange={e => setUserSearchTerm(e.target.value)} placeholder="Search users..." className="w-full pl-8 pr-2 py-2 border border-gray-300 bg-white font-mono text-xs focus:border-gray-500 focus:outline-none" data-id="element-797" />
                    </div>
                  </div>

                  <div className="max-h-64 overflow-y-auto" data-id="element-798">
                    {filteredUsers.map(user => <button key={user.name} onClick={() => {
                  setShowProfileDropdown(false);
                }} className="w-full flex items-center space-x-3 px-3 py-2 hover:bg-gray-50 transition-colors text-left" data-id="element-799">
                        <div className="w-6 h-6 border border-gray-300 flex items-center justify-center text-sm" data-id="element-800">
                          {user.avatar}
                        </div>
                        <span className="font-mono text-xs text-gray-700" data-id="element-801">
                          {user.name}
                        </span>
                      </button>)}
                  </div>

                  <div className="border-t border-gray-200" data-id="element-802">
                    <button onClick={() => {
                  onEditProfile();
                  setShowProfileDropdown(false);
                }} className="w-full flex items-center space-x-2 px-3 py-2 hover:bg-gray-50 transition-colors text-left" data-id="element-803">
                      <Plus className="w-3 h-3 text-gray-400" data-id="element-804" />
                      <span className="font-mono text-xs text-gray-600" data-id="element-805">
                        Create New User
                      </span>
                    </button>
                  </div>
                </div>}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Title - Below Header */}
      <div className="sm:hidden bg-white border-b border-gray-200 px-4 py-3" data-id="element-806">
        <h1 className="text-xl font-bold text-gray-900" data-id="element-807">Recipe Collection</h1>
        <p className="text-xs text-gray-500 font-mono" data-id="element-808">
          Browse and manage your recipes
        </p>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8" data-id="element-809">
        <div className="flex flex-col lg:flex-row gap-8" data-id="element-810">
          {/* Left Sidebar - Filters (Desktop: sidebar, Mobile: top) */}
          <aside className={`${showFilters ? 'block' : 'hidden'} lg:block w-full lg:w-64 flex-shrink-0`} data-id="element-811">
            <div className="space-y-6 lg:sticky lg:top-24" data-id="element-812">
              {/* Search - Always visible */}
              <div className="space-y-2" data-id="element-813">
                <label className="font-mono text-xs uppercase tracking-wider text-gray-500 block" data-id="element-814">
                  Search
                </label>
                <div className="relative" data-id="element-815">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-816" />
                  <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Recipe name..." className="w-full pl-10 pr-3 py-2 border-2 border-gray-300 bg-white font-mono text-xs focus:border-gray-500 focus:outline-none" data-id="element-817" />
                </div>
              </div>

              {/* Sort - Always visible */}
              <div className="space-y-2" data-id="element-818">
                <label className="font-mono text-xs uppercase tracking-wider text-gray-500 block" data-id="element-819">
                  Sort By
                </label>
                <select value={sortBy} onChange={e => setSortBy(e.target.value as any)} className="w-full border-2 border-gray-300 bg-white p-2 font-mono text-xs focus:border-gray-500 focus:outline-none" data-id="element-820">
                  <option value="recent" data-id="element-821">Most Recent</option>
                  <option value="oldest" data-id="element-822">Oldest First</option>
                  <option value="name" data-id="element-823">Name (A-Z)</option>
                </select>
              </div>

              {/* Advanced Filters Toggle - Mobile only */}
              <button onClick={() => setShowAdvancedFilters(!showAdvancedFilters)} className="w-full lg:hidden flex items-center justify-between px-4 py-3 border-2 border-gray-300 hover:border-gray-500 bg-white transition-colors" data-id="element-824">
                <span className="font-mono text-xs uppercase tracking-wider text-gray-600" data-id="element-825">
                  Advanced Filters
                </span>
                {showAdvancedFilters ? <ChevronUp className="w-4 h-4 text-gray-400" data-id="element-826" /> : <ChevronDown className="w-4 h-4 text-gray-400" data-id="element-827" />}
              </button>

              {/* Advanced Filters - Collapsible on mobile, always visible on desktop */}
              <div className={`space-y-6 ${showAdvancedFilters ? 'block' : 'hidden lg:block'}`} data-id="element-828">
                {/* Creator Filter */}
                <div className="space-y-2" data-id="element-829">
                  <label className="font-mono text-xs uppercase tracking-wider text-gray-500 block" data-id="element-830">
                    Created By
                  </label>
                  <div className="space-y-2" data-id="element-831">
                    {CREATORS.map(creator => <button key={creator} onClick={() => toggleCreator(creator)} className="w-full flex items-center space-x-2 cursor-pointer group text-left" data-id="element-832">
                        <div className={`w-4 h-4 border-2 flex items-center justify-center flex-shrink-0 ${selectedCreators.includes(creator) ? 'border-gray-900 bg-gray-900' : 'border-gray-300 group-hover:border-gray-400'}`} data-id="element-833">
                          {selectedCreators.includes(creator) && <div className="w-2 h-2 bg-white" data-id="element-834"></div>}
                        </div>
                        <span className="text-xs font-mono text-gray-700" data-id="element-835">
                          {creator}
                        </span>
                      </button>)}
                  </div>
                </div>

                {/* Difficulty Filter */}
                <div className="space-y-2" data-id="element-836">
                  <label className="font-mono text-xs uppercase tracking-wider text-gray-500 block" data-id="element-837">
                    Difficulty
                  </label>
                  <div className="space-y-2" data-id="element-838">
                    {DIFFICULTIES.map(difficulty => <button key={difficulty} onClick={() => toggleDifficulty(difficulty)} className="w-full flex items-center space-x-2 cursor-pointer group text-left" data-id="element-839">
                        <div className={`w-4 h-4 border-2 flex items-center justify-center flex-shrink-0 ${selectedDifficulties.includes(difficulty) ? 'border-gray-900 bg-gray-900' : 'border-gray-300 group-hover:border-gray-400'}`} data-id="element-840">
                          {selectedDifficulties.includes(difficulty) && <div className="w-2 h-2 bg-white" data-id="element-841"></div>}
                        </div>
                        <span className="text-xs font-mono text-gray-700" data-id="element-842">
                          {difficulty}
                        </span>
                      </button>)}
                  </div>
                </div>

                {/* Cook Time Filter */}
                <div className="space-y-2" data-id="element-843">
                  <label className="font-mono text-xs uppercase tracking-wider text-gray-500 block" data-id="element-844">
                    Max Cook Time (min)
                  </label>
                  <input type="number" value={maxCookTime} onChange={e => setMaxCookTime(e.target.value)} placeholder="Minutes..." className="w-full border-2 border-gray-300 bg-white px-3 py-2 font-mono text-xs focus:border-gray-500 focus:outline-none" data-id="element-845" />
                </div>

                {/* Tags Filter */}
                <div className="space-y-2" data-id="element-846">
                  <label className="font-mono text-xs uppercase tracking-wider text-gray-500 block" data-id="element-847">
                    Tags
                  </label>
                  <div className="flex flex-wrap gap-2" data-id="element-848">
                    {ALL_TAGS.map(tag => <button key={tag} onClick={() => toggleTag(tag)} className={`px-2 py-1 text-xs font-mono border transition-colors ${selectedTags.includes(tag) ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-300 text-gray-600 hover:border-gray-500'}`} data-id="element-849">
                        {tag}
                      </button>)}
                  </div>
                </div>

                {/* Clear Filters */}
                <button onClick={() => {
                setSearchTerm('');
                setSelectedTags([]);
                setSelectedDifficulties([]);
                setSelectedCreators(['You']);
                setMaxCookTime('');
              }} className="w-full py-2 border border-gray-300 hover:border-gray-500 font-mono text-xs text-gray-600 hover:text-gray-900 transition-colors" data-id="element-850">
                  Clear All Filters
                </button>
              </div>
            </div>
          </aside>

          {/* Main Content - Recipe Grid */}
          <main className="flex-1 min-w-0" data-id="element-851">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6" data-id="element-852">
              <h2 className="font-mono text-xs uppercase tracking-wider text-gray-500" data-id="element-853">
                {filteredRecipes.length}{' '}
                {filteredRecipes.length === 1 ? 'Recipe' : 'Recipes'} Found
              </h2>

              <div className="flex items-center gap-3" data-id="element-854">
                <button onClick={() => setShowFilters(!showFilters)} className="flex items-center space-x-2 px-3 py-2 border border-gray-300 hover:border-gray-500 transition-colors lg:hidden" data-id="element-855">
                  <SlidersHorizontal className="w-4 h-4" data-id="element-856" />
                  <span className="font-mono text-xs" data-id="element-857">
                    {showFilters ? 'Hide' : 'Show'} Filters
                  </span>
                </button>

                <button onClick={onCreateRecipe} className="flex items-center space-x-2 px-4 py-2 bg-gray-900 text-white hover:bg-gray-800 transition-colors" data-id="element-858">
                  <Plus className="w-4 h-4" data-id="element-859" />
                  <span className="font-mono text-xs uppercase tracking-widest" data-id="element-860">
                    New Recipe
                  </span>
                </button>
              </div>
            </div>

            {filteredRecipes.length === 0 ? <div className="border-2 border-dashed border-gray-200 p-12 text-center" data-id="element-861">
                <p className="font-mono text-sm text-gray-400" data-id="element-862">
                  No recipes match your filters
                </p>
              </div> : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6" data-id="element-863">
                {filteredRecipes.map(recipe => <div key={recipe.id} className="border-2 border-gray-300 bg-white md:hover:border-gray-500 transition-colors group relative" data-id="element-864">
                    {/* Clickable card content */}
                    <div className="p-4 space-y-3 cursor-pointer" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={e => handleTouchEnd(recipe.id, e)} onClick={e => handleCardClick(recipe.id, e)} data-id="element-865">
                      {/* Thumbnail, Favorite & Add to Cart */}
                      <div className="flex items-start justify-between" data-id="element-866">
                        <div className="w-16 h-16 border-2 border-gray-200 bg-gray-100 flex items-center justify-center text-3xl active:scale-95 transition-transform" data-id="element-867">
                          {recipe.thumbnail}
                        </div>
                        <div className="flex items-center space-x-1 flex-shrink-0" data-id="element-868">
                          <button onClick={e => {
                      e.stopPropagation();
                      toggleFavorite(recipe.id);
                    }} onTouchStart={e => {
                      e.stopPropagation();
                    }} onTouchEnd={e => {
                      e.stopPropagation();
                    }} className="p-2 md:hover:bg-gray-100 active:bg-gray-200 transition-colors" data-id="element-869">
                            <Heart className={`w-4 h-4 ${recipe.isFavorite ? 'fill-gray-900 text-gray-900' : 'text-gray-300'}`} data-id="element-870" />
                          </button>
                          <button onClick={e => {
                      e.stopPropagation();
                      onAddToCart(recipe.id, recipe.title, recipe.thumbnail, recipe.defaultServings, recipe.ingredients);
                    }} onTouchStart={e => {
                      e.stopPropagation();
                    }} onTouchEnd={e => {
                      e.stopPropagation();
                    }} className="p-2 md:hover:bg-gray-100 active:bg-gray-200 transition-colors" title="Add to cart" data-id="element-871">
                            <Plus className="w-4 h-4 text-gray-400 md:hover:text-gray-900" data-id="element-872" />
                          </button>
                        </div>
                      </div>

                      {/* Title & Description */}
                      <div className="select-none" data-id="element-873">
                        <h3 className="font-bold text-lg text-gray-900 leading-tight mb-1 md:group-hover:text-gray-700 transition-colors" data-id="element-874">
                          {recipe.title}
                        </h3>
                        <p className="text-xs text-gray-600 leading-relaxed line-clamp-2" data-id="element-875">
                          {recipe.description}
                        </p>
                      </div>

                      {/* Meta */}
                      <div className="flex items-center space-x-3 text-xs font-mono text-gray-400 select-none" data-id="element-876">
                        <span className="flex items-center space-x-1" data-id="element-877">
                          <Clock className="w-3 h-3" data-id="element-878" />
                          <span data-id="element-879">{recipe.prepTime}</span>
                        </span>
                        <span data-id="element-880">•</span>
                        <span data-id="element-881">{recipe.difficulty}</span>
                      </div>

                      {/* Tags */}
                      <div className="flex flex-wrap gap-1 select-none" data-id="element-882">
                        {recipe.tags.slice(0, 3).map(tag => <span key={tag} className="px-2 py-1 border border-gray-200 text-xs font-mono text-gray-500" data-id="element-883">
                            {tag}
                          </span>)}
                      </div>

                      {/* Creator */}
                      <div className="flex items-center space-x-2 pt-2 border-t border-gray-100 select-none" data-id="element-884">
                        <div className="w-5 h-5 border border-gray-200 flex items-center justify-center text-xs" data-id="element-885">
                          {recipe.createdByAvatar}
                        </div>
                        <span className="text-xs font-mono text-gray-500" data-id="element-886">
                          {recipe.createdBy}
                        </span>
                        <span className="text-xs font-mono text-gray-300" data-id="element-887">
                          •
                        </span>
                        <span className="text-xs font-mono text-gray-400" data-id="element-888">
                          {new Date(recipe.createdOn).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>)}
              </div>}
          </main>
        </div>
      </div>
    </div>;
}